/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { mergeProps, useCalendarCell, useFocusRing } from 'react-aria';
import { type CalendarState, type RangeCalendarState } from 'react-stately';
import {
    isSameDay,
    isSameMonth,
    isToday,
    type CalendarDate,
} from '@internationalized/date';
import cx from 'classnames';

interface CalendarCellProps {
    state: RangeCalendarState | CalendarState;
    date: CalendarDate;
    currentMonth: CalendarDate;
}

export function CalendarCell({ state, date, currentMonth }: CalendarCellProps) {
    const ref = React.useRef<HTMLDivElement>(null);
    const {
        cellProps,
        buttonProps,
        isSelected,
        isOutsideVisibleRange,
        isDisabled,
        formattedDate,
        isInvalid,
        isUnavailable,
        isFocused,
    } = useCalendarCell(
        { date, isDisabled: !isSameMonth(date, currentMonth) },
        state,
        ref
    );

    const isOutsideMonth = !isSameMonth(currentMonth, date);
    const isRangeState = 'highlightedRange' in state;
    const highlightedRange = isRangeState && state.highlightedRange;
    // The start and end date of the selected range will have
    // an emphasized appearance. A single selected date is both.
    const isSelectionStart =
        isSelected &&
        (!isRangeState || (highlightedRange && isSameDay(date, highlightedRange.start)));
    const isSelectionEnd =
        isSelected &&
        (!isRangeState || (highlightedRange && isSameDay(date, highlightedRange.end)));

    const { focusProps, isFocusVisible } = useFocusRing();
    const isHidden = isOutsideMonth || isOutsideVisibleRange;

    /**
     * Backs up react-aria's onPointerEnter range highlight. The browser can send
     * pointerover to a new cell without a pointerout from the old one, and React
     * derives onPointerEnter from pointerout, so the highlight would stay on the
     * previous date until the pointer reached another cell.
     */
    const onPointerOver = (e: React.PointerEvent) => {
        if (!('highlightDate' in state) || !state.anchorDate) {
            return;
        }
        if (e.pointerType === 'touch' && !state.isDragging) {
            return;
        }
        if (isDisabled || isUnavailable || isSameDay(date, state.focusedDate)) {
            return;
        }
        state.highlightDate(date);
    };

    return (
        <div {...cellProps} className="calendar-cell-td">
            <div
                {...mergeProps(buttonProps, focusProps, { onPointerOver })}
                ref={ref}
                hidden={isHidden}
                className={cx('calendar-cell', {
                    'is-today': isToday(date, state.timeZone),
                    'is-selected': isSelected && !isInvalid,
                    'is-invalid': isInvalid,
                    'is-selected-is-invalid': isSelected && isInvalid, // is this even possible??
                    'is-unavailable': isUnavailable,
                    'is-disabled': isDisabled && !isInvalid,
                    'is-focused': isFocused && isFocusVisible,
                    'is-range-start': isSelectionStart && !isInvalid,
                    'is-range-end': isSelectionEnd && !isInvalid,
                })}
            >
                <span className="date">{formattedDate}</span>
            </div>
        </div>
    );
}
